import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const NotificationConfigContext = createContext(null);


const defaultConfig = {

  smtp: {
    enabled: false,
    host: '',
    port: 587,
    secure: false,
    username: '',
    password: '',
    fromEmail: '',
    fromName: 'Covenant Tracker',
    replyTo: '',
  },


  emailTemplates: {
    alertBreach: {
      enabled: true,
      subject: '[URGENT] Breach de covenant détecté - {loanName}',
      template: 'breach',
    },
    alertWarning: {
      enabled: true,
      subject: '[Attention] Covenant approchant du seuil - {loanName}',
      template: 'warning',
    },
    alertInfo: {
      enabled: true,
      subject: '[Info] Notification Covenant Tracker',
      template: 'info',
    },
    reportWeekly: {
      enabled: true,
      subject: 'Rapport hebdomadaire - Covenant Tracker',
      template: 'weekly_report',
    },
    reportMonthly: {
      enabled: false,
      subject: 'Rapport mensuel - Covenant Tracker',
      template: 'monthly_report',
    },
    userInvitation: {
      enabled: true,
      subject: 'Invitation à rejoindre {companyName} sur Covenant Tracker',
      template: 'invitation',
    },
    passwordReset: {
      enabled: true,
      subject: 'Réinitialisation de votre mot de passe - Covenant Tracker',
      template: 'password_reset',
    },
  },


  push: {
    enabled: true,
    vapidPublicKey: '',
    vapidPrivateKey: '',
  },


  webhooks: {
    enabled: false,
    endpoints: [],
  },


  rules: {

    alertsEnabled: true,
    alertBreachEnabled: true,
    alertWarningEnabled: true,
    alertInfoEnabled: true,


    breachChannels: ['email', 'push', 'inApp'],
    warningChannels: ['email', 'inApp'],
    infoChannels: ['inApp'],


    immediateAlerts: true,
    digestEnabled: false,
    digestFrequency: 'daily',
    digestTime: '09:00',


    weeklyReportEnabled: true,
    weeklyReportDay: 1,
    weeklyReportTime: '08:00',
    monthlyReportEnabled: false,
    monthlyReportDay: 1,
    monthlyReportTime: '08:00',


    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
    quietHoursExceptUrgent: true,


    maxEmailsPerDay: 50,
    maxPushPerHour: 10,
  },


  defaultRecipients: {
    adminAlerts: [],
    reportRecipients: [],
  },


  emailBranding: {
    useCompanyBranding: true,
    headerColor: '#2563eb',
    footerText: 'Cet email a été envoyé automatiquement par Covenant Tracker.',
    includeUnsubscribeLink: true,
  },


  logging: {
    enabled: true,
    retentionDays: 30,
  },
};


const dbRowToConfig = (row) => {
  if (!row) return defaultConfig;
  return {
    ...defaultConfig,
    smtp: {
      enabled: row.smtp_enabled ?? defaultConfig.smtp.enabled,
      host: row.smtp_host ?? defaultConfig.smtp.host,
      port: row.smtp_port ?? defaultConfig.smtp.port,
      secure: row.smtp_secure ?? defaultConfig.smtp.secure,
      username: row.smtp_username ?? defaultConfig.smtp.username,
      password: row.smtp_password ?? defaultConfig.smtp.password,
      fromEmail: row.smtp_from_email ?? defaultConfig.smtp.fromEmail,
      fromName: row.smtp_from_name ?? defaultConfig.smtp.fromName,
      replyTo: row.smtp_reply_to ?? defaultConfig.smtp.replyTo,
    },
    webhooks: {
      enabled: row.webhooks_enabled ?? defaultConfig.webhooks.enabled,
      endpoints: row.webhooks_endpoints ?? defaultConfig.webhooks.endpoints,
    },
    rules: {
      ...defaultConfig.rules,
      alertsEnabled: row.alerts_enabled ?? defaultConfig.rules.alertsEnabled,
      alertBreachEnabled: row.alert_breach_enabled ?? defaultConfig.rules.alertBreachEnabled,
      alertWarningEnabled: row.alert_warning_enabled ?? defaultConfig.rules.alertWarningEnabled,
      alertInfoEnabled: row.alert_info_enabled ?? defaultConfig.rules.alertInfoEnabled,
      breachChannels: row.breach_channels ?? defaultConfig.rules.breachChannels,
      warningChannels: row.warning_channels ?? defaultConfig.rules.warningChannels,
      infoChannels: row.info_channels ?? defaultConfig.rules.infoChannels,
      immediateAlerts: row.immediate_alerts ?? defaultConfig.rules.immediateAlerts,
      digestEnabled: row.digest_enabled ?? defaultConfig.rules.digestEnabled,
      digestFrequency: row.digest_frequency ?? defaultConfig.rules.digestFrequency,
      digestTime: row.digest_time ?? defaultConfig.rules.digestTime,
      weeklyReportEnabled: row.weekly_report_enabled ?? defaultConfig.rules.weeklyReportEnabled,
      weeklyReportDay: row.weekly_report_day ?? defaultConfig.rules.weeklyReportDay,
      weeklyReportTime: row.weekly_report_time ?? defaultConfig.rules.weeklyReportTime,
      monthlyReportEnabled: row.monthly_report_enabled ?? defaultConfig.rules.monthlyReportEnabled,
      quietHoursEnabled: row.quiet_hours_enabled ?? defaultConfig.rules.quietHoursEnabled,
      quietHoursStart: row.quiet_hours_start ?? defaultConfig.rules.quietHoursStart,
      quietHoursEnd: row.quiet_hours_end ?? defaultConfig.rules.quietHoursEnd,
      quietHoursExceptUrgent: row.quiet_hours_except_urgent ?? defaultConfig.rules.quietHoursExceptUrgent,
      maxEmailsPerDay: row.max_emails_per_day ?? defaultConfig.rules.maxEmailsPerDay,
      maxPushPerHour: row.max_push_per_hour ?? defaultConfig.rules.maxPushPerHour,
    },
    emailBranding: {
      useCompanyBranding: row.email_branding_use_company ?? defaultConfig.emailBranding.useCompanyBranding,
      headerColor: row.email_branding_header_color ?? defaultConfig.emailBranding.headerColor,
      footerText: row.email_branding_footer_text ?? defaultConfig.emailBranding.footerText,
      includeUnsubscribeLink: row.email_branding_include_unsubscribe ?? defaultConfig.emailBranding.includeUnsubscribeLink,
    },
    logging: {
      enabled: row.logging_enabled ?? defaultConfig.logging.enabled,
      retentionDays: row.logging_retention_days ?? defaultConfig.logging.retentionDays,
    },
    _id: row.id,
  };
};


const configToDbRow = (config) => ({
  smtp_enabled: config.smtp.enabled,
  smtp_host: config.smtp.host,
  smtp_port: config.smtp.port,
  smtp_secure: config.smtp.secure,
  smtp_username: config.smtp.username,
  smtp_password: config.smtp.password,
  smtp_from_email: config.smtp.fromEmail,
  smtp_from_name: config.smtp.fromName,
  smtp_reply_to: config.smtp.replyTo,
  alerts_enabled: config.rules.alertsEnabled,
  alert_breach_enabled: config.rules.alertBreachEnabled,
  alert_warning_enabled: config.rules.alertWarningEnabled,
  alert_info_enabled: config.rules.alertInfoEnabled,
  breach_channels: config.rules.breachChannels,
  warning_channels: config.rules.warningChannels,
  info_channels: config.rules.infoChannels,
  immediate_alerts: config.rules.immediateAlerts,
  digest_enabled: config.rules.digestEnabled,
  digest_frequency: config.rules.digestFrequency,
  digest_time: config.rules.digestTime,
  weekly_report_enabled: config.rules.weeklyReportEnabled,
  weekly_report_day: config.rules.weeklyReportDay,
  weekly_report_time: config.rules.weeklyReportTime,
  monthly_report_enabled: config.rules.monthlyReportEnabled,
  quiet_hours_enabled: config.rules.quietHoursEnabled,
  quiet_hours_start: config.rules.quietHoursStart,
  quiet_hours_end: config.rules.quietHoursEnd,
  quiet_hours_except_urgent: config.rules.quietHoursExceptUrgent,
  max_emails_per_day: config.rules.maxEmailsPerDay,
  max_push_per_hour: config.rules.maxPushPerHour,
  webhooks_enabled: config.webhooks.enabled,
  webhooks_endpoints: config.webhooks.endpoints,
  email_branding_use_company: config.emailBranding.useCompanyBranding,
  email_branding_header_color: config.emailBranding.headerColor,
  email_branding_footer_text: config.emailBranding.footerText,
  email_branding_include_unsubscribe: config.emailBranding.includeUnsubscribeLink,
  logging_enabled: config.logging.enabled,
  logging_retention_days: config.logging.retentionDays,
});

export function NotificationConfigProvider({ children }) {
  const [config, setConfig] = useState(defaultConfig);
  const [testStatus, setTestStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [configId, setConfigId] = useState(null);


  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('notification_config')
        .select('*')
        .limit(1);


      if (error && error.code !== 'PGRST116') {

        if (!error.message?.includes('406')) {
          console.error('Error loading notification config:', error);
        }
      }

      if (data && data.length > 0) {
        setConfig(dbRowToConfig(data[0]));
        setConfigId(data[0].id);
      }
    } catch (err) {

      console.log('Notification config not available:', err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);


  const saveConfigToSupabase = useCallback(async (newConfig) => {
    try {
      const dbRow = configToDbRow(newConfig);

      if (configId) {
        const { error } = await supabase
          .from('notification_config')
          .update(dbRow)
          .eq('id', configId);
        if (error) console.error('Error updating notification config:', error);
      } else {
        const { data, error } = await supabase
          .from('notification_config')
          .insert([dbRow])
          .select()
          .single();
        if (error) {
          console.error('Error inserting notification config:', error);
        } else if (data) {
          setConfigId(data.id);
        }
      }
    } catch (err) {
      console.error('Error saving notification config:', err);
    }
  }, [configId]);


  const updateConfig = useCallback((updates) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      saveConfigToSupabase(newConfig);
      return newConfig;
    });
  }, [saveConfigToSupabase]);


  const updateSmtpConfig = useCallback((smtpUpdates) => {
    setConfig(prev => {
      const newConfig = { ...prev, smtp: { ...prev.smtp, ...smtpUpdates } };
      saveConfigToSupabase(newConfig);
      return newConfig;
    });
  }, [saveConfigToSupabase]);


  const updateRules = useCallback((rulesUpdates) => {
    setConfig(prev => {
      const newConfig = { ...prev, rules: { ...prev.rules, ...rulesUpdates } };
      saveConfigToSupabase(newConfig);
      return newConfig;
    });
  }, [saveConfigToSupabase]);


  const updateEmailTemplate = useCallback((templateKey, templateUpdates) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        emailTemplates: {
          ...prev.emailTemplates,
          [templateKey]: { ...prev.emailTemplates[templateKey], ...templateUpdates },
        },
      };
      saveConfigToSupabase(newConfig);
      return newConfig;
    });
  }, [saveConfigToSupabase]);


  const updateEmailBranding = useCallback((brandingUpdates) => {
    setConfig(prev => {
      const newConfig = { ...prev, emailBranding: { ...prev.emailBranding, ...brandingUpdates } };
      saveConfigToSupabase(newConfig);
      return newConfig;
    });
  }, [saveConfigToSupabase]);


  const addWebhook = useCallback((webhook) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        webhooks: {
          ...prev.webhooks,
          endpoints: [...prev.webhooks.endpoints, { id: Date.now(), ...webhook }],
        },
      };
      saveConfigToSupabase(newConfig);
      return newConfig;
    });
  }, [saveConfigToSupabase]);


  const removeWebhook = useCallback((webhookId) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        webhooks: {
          ...prev.webhooks,
          endpoints: prev.webhooks.endpoints.filter(w => w.id !== webhookId),
        },
      };
      saveConfigToSupabase(newConfig);
      return newConfig;
    });
  }, [saveConfigToSupabase]);


  const testSmtpConnection = async () => {
    setTestStatus({ status: 'testing', message: 'Test de connexion en cours...' });


    await new Promise(resolve => setTimeout(resolve, 2000));

    if (!config.smtp.host || !config.smtp.username) {
      setTestStatus({
        status: 'error',
        message: 'Configuration SMTP incomplète. Veuillez remplir tous les champs requis.',
      });
      return false;
    }


    const success = config.smtp.host && config.smtp.port;

    if (success) {
      setTestStatus({
        status: 'success',
        message: 'Connexion SMTP réussie ! Un email de test a été envoyé.',
      });
      return true;
    } else {
      setTestStatus({
        status: 'error',
        message: 'Échec de la connexion. Vérifiez vos paramètres SMTP.',
      });
      return false;
    }
  };


  const sendTestEmail = async (toEmail) => {
    setTestStatus({ status: 'sending', message: 'Envoi de l\'email de test...' });

    await new Promise(resolve => setTimeout(resolve, 1500));

    setTestStatus({
      status: 'success',
      message: `Email de test envoyé à ${toEmail}`,
    });

    return true;
  };


  const resetConfig = () => {
    setConfig(defaultConfig);
    setTestStatus(null);
  };


  const getConfigSummary = () => {
    const enabledChannels = [];
    if (config.smtp.enabled) enabledChannels.push('Email');
    if (config.push.enabled) enabledChannels.push('Push');
    if (config.webhooks.enabled) enabledChannels.push('Webhooks');

    return {
      channelsEnabled: enabledChannels,
      smtpConfigured: !!(config.smtp.host && config.smtp.username),
      alertsEnabled: config.rules.alertsEnabled,
      weeklyReportEnabled: config.rules.weeklyReportEnabled,
      webhooksCount: config.webhooks.endpoints.length,
    };
  };

  const value = {
    config,
    testStatus,
    isLoading,
    updateConfig,
    updateSmtpConfig,
    updateRules,
    updateEmailTemplate,
    updateEmailBranding,
    addWebhook,
    removeWebhook,
    testSmtpConnection,
    sendTestEmail,
    resetConfig,
    getConfigSummary,
    loadConfig,
  };

  return (
    <NotificationConfigContext.Provider value={value}>
      {children}
    </NotificationConfigContext.Provider>
  );
}

export function useNotificationConfig() {
  const context = useContext(NotificationConfigContext);
  if (!context) {
    throw new Error('useNotificationConfig must be used within a NotificationConfigProvider');
  }
  return context;
}

export default NotificationConfigContext;